import React, { createContext, useCallback, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import CommandPalette from './CommandPalette';
import type { CommandContext, CommandItem, CommandPaletteApi } from './CommandPalette.types';
import { CommandRegistry } from './registry';

export const CommandPaletteContext = createContext<CommandPaletteApi | null>(null);

interface CommandPaletteProviderProps {
  children: React.ReactNode;
}

const CommandPaletteProvider = ({ children }: CommandPaletteProviderProps) => {
  const location = useLocation();
  const registryRef = useRef(new CommandRegistry());

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [registryVersion, setRegistryVersion] = useState(0);
  const [isRunning, setIsRunning] = useState(false);

  const registerCommands = useCallback((source: string, commands: CommandItem[]) => {
    const dispose = registryRef.current.register(source, commands);
    setRegistryVersion((prev) => prev + 1);

    return () => {
      dispose();
      setRegistryVersion((prev) => prev + 1);
    };
  }, []);

  const openPalette = useCallback((initialQuery?: string) => {
    setQuery(initialQuery ?? '');
    setActiveIndex(0);
    setOpen(true);
  }, []);

  const closePalette = useCallback(() => {
    setOpen(false);
    setQuery('');
    setActiveIndex(0);
  }, []);

  const contextValue = useMemo<CommandContext>(() => {
    return {
      route: location.pathname,
    };
  }, [location.pathname]);

  const commands = useMemo(() => {
    return registryRef.current.getAll();
  }, [registryVersion]);

  const apiValue = useMemo<CommandPaletteApi>(() => {
    return {
      registerCommands,
      openPalette,
      closePalette,
    };
  }, [closePalette, openPalette, registerCommands]);

  const runCommand = useCallback(async (command: CommandItem) => {
    if (isRunning) {
      return;
    }

    setIsRunning(true);

    try {
      await command.run(contextValue);
      closePalette();
    } catch (error) {
      console.error('[CommandPalette] Command execution failed:', error);
    } finally {
      setIsRunning(false);
    }
  }, [closePalette, contextValue, isRunning]);

  return (
    <CommandPaletteContext.Provider value={apiValue}>
      {children}
      <CommandPalette
        open={open}
        query={query}
        setQuery={setQuery}
        commands={commands}
        context={contextValue}
        activeIndex={activeIndex}
        setActiveIndex={setActiveIndex}
        onClose={closePalette}
        onRun={runCommand}
      />
    </CommandPaletteContext.Provider>
  );
};

export default CommandPaletteProvider;
